/*
Collection of functions for manipulating dictionaries
 */
export function filterDict<V>(
  dict: { [key: string]: V },
  filter: (key: string) => boolean
): { [key: string]: V } {
  return Object.keys(dict)
    .filter(filter)
    .reduce((cur, key) => {
      return Object.assign(cur, { [key]: dict[key] });
    }, {});
}

export function filterDictByValue<V>(
  dict: { [key: string]: V },
  filter: (value: V) => boolean
): { [key: string]: V } {
  return Object.entries(dict)
    .filter(([, value]) => filter(value))
    .reduce((cur, [key, value]) => {
      return Object.assign(cur, { [key]: value });
    }, {});
}

const identity = (x: any) => x;

export function dictionaryMap<V, V2>(
  dict: { [key: string]: V },
  keyMap: (key: string) => string,
  valMap: (val: V) => V2
): { [key: string]: V2 } {
  return Object.entries(dict).reduce(
    (prev, [key, val]) => Object.assign(prev, { [keyMap(key)]: valMap(val) }),
    {}
  );
}

export function dictionaryEntryMap<V, V2>(
  dict: { [key: string]: V },
  entryMap: (key: string, value: V) => { [newKey: string]: V2 }
): { [key: string]: V2 } {
  return Object.entries(dict).reduce(
    (prev, [key, val]) => Object.assign(prev, entryMap(key, val)),
    {}
  );
}

export function dictionaryKeyMap<V>(
  dict: { [key: string]: V },
  keyMap: (key: string) => string
): { [key: string]: V } {
  return dictionaryMap(dict, keyMap, identity);
}

export function dictionaryValueMap<V, V2>(
  dict: { [key: string]: V },
  valMap: (val: V) => V2
): { [key: string]: V2 } {
  return dictionaryMap(dict, identity, valMap);
}

export function dictionaryReverse(
  dict: { [key: string]: string },
  keyToValueMapper: (key: string) => any = (key: string) => key
): {
  [key: string]: any;
} {
  const init: { [key: string]: string } = {};
  return Object.entries(dict).reduce((prev, [k, v]) => {
    prev[v] = keyToValueMapper(k);
    return prev;
  }, init);
}

/** Functions to generate dictionaries from other types */
export function arrayToDict<T>(
  array: T[],
  startIndex: number = 1
): { [idx: number]: T } {
  const init: { [idx: number]: T } = {};
  let idx = startIndex;
  return array.reduce((prev, curr) => {
    prev[idx] = curr;
    idx += 1;
    return prev;
  }, init);
}

/**
 * Class to map values to new values and auto generate a new mapping
 * if one doesn't already exist
 */
export class PersistentKeyMapper {
  _map: { [key: string]: string };
  _initializer: (key: string) => string;

  constructor(_initializer: (key: string) => string) {
    this._initializer = _initializer;
    this._map = {};
  }

  map(key: string): string {
    if (!(key in this._map)) {
      this._map[key] = this._initializer(key);
    }
    return this._map[key];
  }

  mapNullable(key: string | undefined): string | undefined {
    if (key === null || key === undefined) {
      return key;
    }
    return this.map(key);
  }

  has(key: string): boolean {
    return key in this._map;
  }
}
